import React, { useState, useEffect, useContext, useRef } from 'react';
import { Bar } from 'react-chartjs-2';
import 'react-datepicker/dist/react-datepicker.css';
import AppContext from '../../contexts/AppContext';
import dayjs from 'dayjs';
import { FaChevronDown } from 'react-icons/fa';
import { hasElevatedSessionAccess } from '../../utils/sessionPermissions';
import { shareText } from './compendiumAnalyticsData';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const ContentLogAnalysis = () => {
    const { token, selectedRole, userInfo } = useContext(AppContext);

    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    const sevenDaysAgo = new Date(yesterday);
    sevenDaysAgo.setDate(yesterday.getDate() - 6);
    const [startDate, setStartDate] = useState(sevenDaysAgo);
    const [endDate, setEndDate] = useState(yesterday);
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(0); // which block of ten the ranking shows
    const changedAt = useRef(0); // when the ranking last changed, so a PDF is not made while its chart is still being drawn
    const [timeRange, setTimeRange] = useState("7"); // 默认为过去 7 天

    const roleArray = userInfo?.roleInfo?.[selectedRole?.type] || [];
    const currentRole = roleArray.find((r) => r.id === selectedRole?.id);
    const permissionLevel = currentRole?.permissionLevel;
    const canShowAdvanced = hasElevatedSessionAccess(userInfo, permissionLevel);


    const handleTimeRangeChange = (e) => {
        const range = e.target.value;
        setTimeRange(range);

        const newEndDate = new Date(yesterday);
        let newStartDate = new Date(newEndDate);

        if (range === "7") {
            newStartDate.setDate(newEndDate.getDate() - 6);
        } else if (range === "14") {
            newStartDate.setDate(newEndDate.getDate() - 13);
        } else if (range === "30") {
            newStartDate.setMonth(newEndDate.getMonth() - 1);
            newStartDate.setDate(newStartDate.getDate() + 1);
        } else if (range === "90") {
            newStartDate.setMonth(newEndDate.getMonth() - 3);
            newStartDate.setDate(newStartDate.getDate() + 1);
        } else if (range === "180") {
            newStartDate.setMonth(newEndDate.getMonth() - 6);
            newStartDate.setDate(newStartDate.getDate() + 1);
        } else if (range === "365") {
            newStartDate.setFullYear(newEndDate.getFullYear() - 1);
            newStartDate.setDate(newStartDate.getDate() + 1);
        }
        setStartDate(newStartDate);
        setEndDate(newEndDate);
    };

    useEffect(() => {
        // Set when the venue or period changes (or the page closes): an answer that arrives after that belongs to the
        // old choice and must not replace the ranking of the new one.
        let outdated = false;
        const fetchAnalyticsData = async () => {
            if (!startDate || !endDate || !selectedRole?.id) {
                console.error('Missing required parameters:', { startDate, endDate, selectedRole });
                return;
            }

            setLoading(true);

            try {
                const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/private/analysis/content/log/summary/read`;

                // 1. 先复制一下 startDate / endDate，免得直接修改它们的 state
                const localStart = new Date(startDate);
                const localEnd = new Date(endDate);

                // 2. 把开始日期修正到当日 00:00:00.000
                localStart.setHours(0, 0, 0, 0);

                // 3. 把结束日期修正到当日 23:59:59.999
                localEnd.setHours(23, 59, 59, 999);

                const response = await fetch(apiUrl, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({
                        venue_id: selectedRole.id,
                        startDate: dayjs(localStart).format("YYYY-MM-DDTHH:mm:ss.SSS"),
                        endDate: dayjs(localEnd).format("YYYY-MM-DDTHH:mm:ss.SSS"),
                        userTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                    }),
                });

                if (response.ok) {
                    const result = await response.json();
                    if (outdated) return;
                    changedAt.current = Date.now();
                    setData(result);
                    setPage(0); // a new period or venue starts again at the top of the ranking
                } else {
                    if (outdated) return;
                    console.error('Failed to fetch data:', response.statusText);
                    setData([]); // never leave the ranking of the previous venue or period under the new choice
                }
            } catch (error) {
                if (outdated) return;
                console.error('Error fetching data:', error);
                setData([]);
            } finally {
                if (!outdated) setLoading(false);
            }
        };

        fetchAnalyticsData();
        return () => { outdated = true; };
    }, [startDate, endDate, selectedRole, token]);

    if (loading) {
        // data-report-extra: the Dashboard's "Download PDF" puts this chart on the page after the browsing chart
        return <div data-report-extra="true" data-report-key="loading">Loading...</div>;
    }


    const processedContentData = Object.entries(data.reduce((acc, entry) => {
        for (const [name, value] of Object.entries(entry.Content || {})) {
            acc[name] = (acc[name] || 0) + value;
        }
        return acc;
    }, {}))
        .sort((a, b) => b[1] - a[1]); // 按数量降序排列

    // Each bar is that content's share of all content clicks in the period (every item, not just the ones on this page).
    const totalClicks = processedContentData.reduce((sum, [, value]) => sum + value, 0);
    const totalContentCount = processedContentData.length;
    const pageSize = 10;

    // 动态生成分页区间
    const rangeOptions = [];
    for (let i = 0; i < totalContentCount; i += pageSize) {
        const start = i + 1;
        const end = Math.min(i + pageSize, totalContentCount);
        rangeOptions.push({ start, end });
    }

    // 当前分页数据
    const shownPage = Math.min(page, Math.max(rangeOptions.length - 1, 0));
    const currentContentData = processedContentData.slice(shownPage * pageSize, (shownPage + 1) * pageSize);
    const contentLabels = currentContentData.map(([name]) => name);
    const contentValues = currentContentData.map(([, value]) => (totalClicks ? Math.round(value / totalClicks * 1000) / 10 : 0));
    const percentLabel = (percent) => shareText(percent, 100);

    const handleRangeChange = (event) => {
        changedAt.current = Date.now();
        setPage(Number(event.target.value));
    };

    return (
        <div
            data-report-extra="true"
            data-report-key={`${selectedRole?.id}|${timeRange}|${shownPage}|${totalClicks}`}
            data-report-changed-at={changedAt.current}
            className="w-full p-2 bg-gray-50 rounded-lg shadow-lg"
        >

            <div className="flex gap-4 items-center mt-4">

                {/* 时间范围下拉菜单 */}
                <label className="ml-4 block">
                    <span className="text-[0.78rem] font-semibold uppercase tracking-[0.16em] text-[#7f90b3]">
                        Time Range
                    </span>
                    <div className="relative mt-2 inline-flex items-center">
                        <select
                            value={timeRange}
                            onChange={handleTimeRangeChange}
                            className="h-11 w-44 appearance-none rounded-xl border border-[#dbe3f5] bg-white py-2 pl-4 pr-10 text-[15px] font-medium text-[#24324d] shadow-none outline-none transition-all duration-200 hover:border-[#c8d3fb] hover:bg-[#f8faff] focus:border-[#aebcf1] focus:ring-4 focus:ring-[rgba(74,92,255,0.08)]"
                        >
                            <option value="7">Past 7 Days</option>
                            <option value="14">Past 14 Days</option>
                            <option value="30">Past 30 Days</option>
                            {canShowAdvanced && (
                                <>
                                    <option value="90">Past 3 Months</option>
                                    <option value="180">Past 6 Months</option>
                                    <option value="365">Past 12 Months</option>
                                </>
                            )}
                        </select>
                        <span className="pointer-events-none absolute right-3 inline-flex h-6 w-6 items-center justify-center rounded-full bg-[#eef2ff] text-[#4a63ff]">
                            <FaChevronDown className="h-3 w-3" />
                        </span>
                    </div>
                </label>
                <label className="ml-4 block">
                    <span className="text-[0.78rem] font-semibold uppercase tracking-[0.16em] text-[#7f90b3]">
                        Select Range
                    </span>
                    <div className="relative mt-2 inline-flex items-center">
                        <select
                            value={shownPage}
                            onChange={handleRangeChange}
                            className="h-11 w-52 appearance-none rounded-xl border border-[#dbe3f5] bg-white py-2 pl-4 pr-10 text-[15px] font-medium text-[#24324d] shadow-none outline-none transition-all duration-200 hover:border-[#c8d3fb] hover:bg-[#f8faff] focus:border-[#aebcf1] focus:ring-4 focus:ring-[rgba(74,92,255,0.08)]"
                            disabled={rangeOptions.length === 0}
                        >
                            {rangeOptions.length === 0 ? (
                                <option>No data available</option>
                            ) : (
                                rangeOptions.map(({ start, end }, index) => (
                                    <option key={`${start}-${end}`} value={index}>
                                        {`${start} - ${end}`}
                                    </option>
                                ))
                            )}
                        </select>
                        <span className="pointer-events-none absolute right-3 inline-flex h-6 w-6 items-center justify-center rounded-full bg-[#eef2ff] text-[#4a63ff]">
                            <FaChevronDown className="h-3 w-3" />
                        </span>
                    </div>
                </label>



            </div>

            {/* Content Trends */}
            <div className=" w-full bg-gray-50 p-4">
                <h3 className="text-lg font-semibold text-gray-700 text-center mb-4">Concierge Content Ranking

                    <span className="ml-2 relative group cursor-pointer">
                        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gray-300 text-gray-700 text-sm font-bold">
                            ?
                        </span>
                        {/* Tooltip */}
                        <div className="absolute hidden group-hover:block bg-white text-gray-700 text-sm p-2 rounded shadow-md whitespace-normal w-[400px] -top-10 left-3/4 transform translate-x-[10%] text-justify">
                            This chart displays the ranking of content (e.g., Westpac Bank under the Bank category) shown as a share of all content clicks within the selected time range, helping you identify the most popular content.
                        </div>
                    </span>

                </h3>


                {currentContentData.length > 0 ? (
                    <div className="w-full h-[70vh]">
                        <Bar
                            data={{
                                labels: contentLabels,
                                datasets: [
                                    {
                                        label: 'Share of content clicks',
                                        data: contentValues,
                                        backgroundColor: '#54728C',
                                        borderColor: '#2A3E59',
                                        borderWidth: 1,
                                        maxBarThickness: 30, // 控制每个柱子的最大宽度
                                    },
                                ],
                            }}
                            options={{
                                indexAxis: 'y', // 横向条形图
                                responsive: true,
                                maintainAspectRatio: false,
                                plugins: {
                                    legend: {
                                        display: false // 禁用点击隐藏数据功能
                                    },
                                    tooltip: {
                                        callbacks: {
                                            label: (context) => `${percentLabel(context.parsed.x)} of content clicks`,
                                        },
                                    },
                                    datalabels: {
                                        color: '#24324d', // dark text just past the end of the bar, so it can be read on short bars too
                                        anchor: 'end',
                                        align: 'right',
                                        clip: false,
                                        formatter: percentLabel,
                                        font: {
                                            size: 10, // 调整数字字体大小
                                            weight: 'bold', // 设置数字字体加粗
                                        },
                                    },
                                },
                                scales: {
                                    x: {
                                        beginAtZero: true,
                                        max: Math.min(100, Math.ceil(Math.max(...contentValues) / 10) * 10), // as far as the longest bar needs, never past 100%
                                        ticks: {
                                            callback: (value) => `${value}%`,
                                        },
                                        title: {
                                            display: true, // 显示标题
                                            text: 'Share of content clicks', // x轴单位
                                            font: {
                                                size: 14, // 设置字体大小
                                                weight: 'bold', // 字体加粗
                                            },
                                            color: '#4B5563', // 设置标题颜色
                                        },
                                    },
                                    y: {
                                        ticks: {
                                            padding: 1,
                                            callback: function (value, index, values) {
                                                const label = this.getLabelForValue(value);
                                                if (window.innerWidth < 768 && label.length > 10) {
                                                    return label.slice(0, 10) + '…';
                                                }
                                                return label;
                                            }
                                        },
                                        categoryPercentage: 2, // 调整每个分类的宽度百分比（越小间距越小）
                                        barPercentage: 0.8, // 调整每个条形的宽度百分比
                                        title: {
                                            display: true, // 显示标题
                                            text: 'Content Title', // y轴单位
                                            font: {
                                                size: 14, // 设置字体大小
                                                weight: 'bold', // 字体加粗
                                            },
                                            color: '#4B5563', // 设置标题颜色
                                        },
                                    }
                                },
                                layout: {
                                    padding: {
                                        top: 5, // 图表顶部的间距
                                        bottom: 5, // 图表底部的间距
                                        right: 40, // room for the percentage written after the longest bar
                                    },
                                },
                            }}
                        />
                    </div>

                ) : (
                    <p className="text-lg font-semibold text-gray-500 text-center">
                        No content data available for the selected time range.
                    </p>
                )}

            </div>
        </div>
    );
};

export default ContentLogAnalysis;
